// 11 Sep 1906

// Unit conversion utilities
export const VITAL_UNIT_NORMALIZATION_VERSION = 1

const hasValue = (value) => value !== null && value !== undefined && value !== ''

export const convertValue = (value, fromUnit, toUnit) => {
  if (!hasValue(value) || !fromUnit || !toUnit || fromUnit.code === toUnit.code) {
    return value
  }

  // Convert from input unit to base unit
  let baseValue = value
  if (fromUnit.toBase) {
    if (fromUnit.toBase.kind === 'linearCombo') {
      // Handle composite units like feet/inches
      baseValue = 0
      fromUnit.toBase.parts.forEach((part) => {
        const partValue = value[part.key] || 0
        baseValue += partValue * part.factor
      })
      if (fromUnit.toBase.offset) {
        baseValue += fromUnit.toBase.offset
      }
    } else {
      // Handle simple factor/offset conversion
      baseValue = value * fromUnit.toBase.factor + (fromUnit.toBase.offset || 0)
    }
  }

  // Convert from base unit to target unit
  let result = baseValue
  if (toUnit.toBase) {
    if (toUnit.toBase.kind === 'linearCombo') {
      // Handle composite units like feet/inches - this is for display/conversion back
      // This is complex and would need specific logic for each composite type
      result = baseValue
    } else {
      // Handle simple factor/offset conversion
      const offset = toUnit.toBase.offset || 0
      result = (baseValue - offset) / toUnit.toBase.factor
    }
  }

  // Fix floating-point precision issues by rounding to reasonable precision
  // Use 10 decimal places as intermediate precision, then let display logic handle final rounding
  return Math.round(result * 10000000000) / 10000000000
}

// Get default unit for a field
export const getDefaultUnit = (field) => {
  if (!field.units || field.units.length === 0) return null
  return field.units.find((unit) => unit.default) || field.units[0]
}

// Convert value to base unit for storage
export const convertToBaseUnit = (value, field, selectedUnit) => {
  if (!hasValue(value) || !field || !selectedUnit) return null

  const baseUnit = { code: field.baseUnit, toBase: { factor: 1, offset: 0 } }
  return convertValue(value, selectedUnit, baseUnit)
}

// Convert value from base unit for display
export const convertFromBaseUnit = (value, field, targetUnit) => {
  if (!hasValue(value) || !field || !targetUnit) return value

  const baseUnit = { code: field.baseUnit, toBase: { factor: 1, offset: 0 } }
  return convertValue(value, baseUnit, targetUnit)
}

// Keep canonical values at the top level for backwards compatibility, while also sending the
// original display value and unit so the server can authoritatively repeat the conversion.
// inputValuesByVitalId is request-only on the server; only unit provenance is persisted.
export const buildUnitAwareVitalPayload = ({ vitalData = {}, fields = [], selectedUnits = {} }) => {
  const inputValuesByVitalId = {}
  const displayUnitsByVitalId = {}
  const canonicalUnitsByVitalId = {}

  fields.forEach((field) => {
    const canonicalValue = vitalData[field.id]
    const selectedUnit = selectedUnits[field.id] || getDefaultUnit(field)

    if (
      !hasValue(canonicalValue) ||
      typeof canonicalValue !== 'number' ||
      field.type === 'calculated' ||
      !selectedUnit?.code ||
      !field.baseUnit
    ) {
      return
    }

    inputValuesByVitalId[field.id] = convertFromBaseUnit(canonicalValue, field, selectedUnit)
    displayUnitsByVitalId[field.id] = selectedUnit.code
    canonicalUnitsByVitalId[field.id] = field.baseUnit
  })

  return {
    ...vitalData,
    unitNormalizationVersion: VITAL_UNIT_NORMALIZATION_VERSION,
    inputValuesByVitalId,
    displayUnitsByVitalId,
    canonicalUnitsByVitalId,
  }
}

// Field processing utilities
export const processFieldsForDisplay = (fieldsData) => {
  if (!Array.isArray(fieldsData)) return []

  // Filter enabled fields only
  const enabledFields = fieldsData.filter((field) => field.enabled && !field.deleted)

  // Sort by section order first, then by field order
  return enabledFields.sort((a, b) => {
    if (a.sectionOrder !== b.sectionOrder) {
      return a.sectionOrder - b.sectionOrder
    }
    return a.order - b.order
  })
}

// Group fields by section or groupId
export const groupFields = (fields) => {
  const grouped = {}

  fields.forEach((field) => {
    const groupKey = field.ui?.groupId || field.section || 'default'
    if (!grouped[groupKey]) {
      grouped[groupKey] = []
    }
    grouped[groupKey].push(field)
  })

  return grouped
}

// Calculate field values (BMI, waist-hip ratio, etc.)
export const calculateFieldValue = (field, allValues) => {
  if (field.type !== 'calculated' || !field.compute) {
    return null
  }

  const dependencies = {}
  let hasAllDependencies = true

  // Collect dependency values
  field.compute.dependsOn.forEach((dep) => {
    const depValue = allValues[dep.code]
    if (depValue === null || depValue === undefined) {
      hasAllDependencies = false
      return
    }
    dependencies[dep.as] = depValue
  })

  if (!hasAllDependencies) {
    return null
  }

  try {
    // Simple expression evaluation - in a real app, use a safer expression evaluator
    let expression = field.compute.expression

    // Replace variables with actual values
    Object.keys(dependencies).forEach((varName) => {
      const regex = new RegExp(`\\b${varName}\\b`, 'g')
      expression = expression.replace(regex, dependencies[varName])
    })

    // Basic math evaluation (unsafe - should use a proper expression evaluator)
    const result = eval(expression)

    // Apply decimals rounding
    const decimals = getDefaultUnit(field)?.decimals || 2
    return parseFloat(result.toFixed(decimals))
  } catch (error) {
    console.error('Error calculating field value:', error)
    return null
  }
}

// Validation utilities
export const validateField = (field, value, allValues) => {
  if (!field.validators || field.validators.length === 0) {
    return { isValid: true, message: null }
  }

  for (const validator of field.validators) {
    switch (validator.kind) {
      case 'pairRequired': {
        const otherValue = allValues[validator.other]
        if ((value && !otherValue) || (!value && otherValue)) {
          return { isValid: false, message: validator.message }
        }
        break
      }

      case 'crossField': {
        // A cross-field rule only makes sense once this field has a value; with the field empty
        // the placeholder 0 would make expressions like "value < systolic" fail spuriously.
        if (value === null || value === undefined || value === '') {
          break
        }
        const otherFieldCodes = validator.expr
          ? Object.keys(allValues).filter(
              (fieldKey) => fieldKey !== field.id && new RegExp(`\\b${fieldKey}\\b`).test(validator.expr),
            )
          : []
        const referencedValueMissing = otherFieldCodes.some(
          (fieldKey) =>
            allValues[fieldKey] === null ||
            allValues[fieldKey] === undefined ||
            allValues[fieldKey] === '',
        )
        // Partner value not entered (pairRequired reports that case) — nothing to compare yet.
        if (referencedValueMissing) {
          break
        }
        try {
          let expression = validator.expr
          // Replace variables
          Object.keys(allValues).forEach((fieldKey) => {
            if (fieldKey !== field.id) {
              const regex = new RegExp(`\\b${fieldKey}\\b`, 'g')
              expression = expression.replace(regex, allValues[fieldKey] || 0)
            }
          })
          expression = expression.replace(/\bvalue\b/g, value || 0)

          const isValid = eval(expression)
          if (!isValid) {
            return { isValid: false, message: validator.message }
          }
        } catch (error) {
          console.error('Cross-field validation error:', error)
        }
        break
      }
    }
  }

  return { isValid: true, message: null }
}

// Check if field has any data
export const hasVitalData = (vitals, fields) => {
  if (!vitals || !fields) return false

  return fields.some((field) => {
    const value = vitals[field.id]
    return value !== null && value !== undefined && value !== ''
  })
}

// Initialize vital data state from fields
export const initializeVitalData = (fields) => {
  const initialData = {}
  fields.forEach((field) => {
    initialData[field.id] = null
  })
  return initialData
}
